import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchStores,
  modifyStore,
  removeStore,
} from "../../../../redux/Thunk/Store/StoreThunks";
import Button from "../../../common/Button";
import BillingSettings from "../BillingSettings/BillingSettings";
import Address from "./Address";
import Compliances from "./Compliances";
import Contact from "./Contact";
import Location from "./Location";
import PrimaryDetails from "./PrimaryDetails";
import SearchSelectStore from "./SearchSelectStore";
import { setStoreById } from "../../../../redux/Reducer/Store/StoreSlice";
import toast from "react-hot-toast";
import { createStoreValidation } from "../../../common/Validation/createOrderValidation";
import Warning from "../../../common/icons/Warning";
import Edit from "../../../common/icons/Edit";
import Loader from "../../../Loader";

const Store = () => {
  const dispatch = useDispatch();
  const [selectedStore, setSelectedStore] = useState({});
  const [editingField, setEditingField] = useState(null); // Tracks the field being edited
  const [showEditIcon, setShowEditIcon] = useState(false); // Tracks whether to show the edit icon

  useEffect(() => {
    dispatch(setStoreById({}));
    setShowEditIcon(false);
  }, [dispatch]);

  useEffect(() => {
    setShowEditIcon(false);
  }, [selectedStore]);

  const { stores, storeById, loading } = useSelector(
    ({ store }) => ({
      stores: store?.stores,
      storeById: store?.storeById,
      loading: store?.loading,
    }),
    shallowEqual
  );
  const handleStoreSubmit = async (values, resetForm) => {
    const storeId = values?.storeDetails?.SID;
    let gst = values?.compliances?.gstin;
    values.compliances.gstin = gst.trim() === "" ? null : gst.trim();
    try {
      const response = await dispatch(
        modifyStore({ storeId, updatedData: values })
      ).unwrap();

      if (response?.status === 200) {
        setShowEditIcon(false);
        dispatch(setStoreById({}));
        setSelectedStore({});
        resetForm({
          values: initialValues,
        });
        toast.success("Store updated successfully");
      } else {
        if (response?.response?.data?.error === "Username already exists.") {
          handleFocus("storeDetails.username");
          toast.error(response?.response?.data?.error);
        } else if (
          response?.response?.data?.error ===
          `gst number already exist in the system.`
        ) {
          handleFocus("storeCompliance.gstin");
          toast.error(response?.response?.data?.error);
        } else {
          toast.error(response?.message);
        }
        // Handle failure if needed
      }
    } catch (error) {
      console.error("Complete Error Object:", error);

      if (error.response) {
        if (error) {
          console.error("API Error:", error.error);
        } else {
          console.error("Unexpected Error:", error);
          toast.error("An unexpected error occurred.");
        }
      }
    }
  };
  // const handleFocus = (feild) => {
  //   if (feild === "user") {
  //     const inputField = document.getElementById("username");
  //     inputField?.focus();
  //   } else if (feild === "gst") {
  //     const inputField = document.getElementById("gst");
  //     inputField?.focus();
  //   }
  // };
  const storeDetails = storeById;
  const initialValues = {
    storeDetails: {
      SID: storeDetails?.SID || "",
      title: storeDetails?.title || "",
      username: storeDetails?.username || "",
      pin: storeDetails?.pin || "",
    },
    compliances: {
      gstin: storeDetails?.storeCompliance?.gstin || "",
      dln: storeDetails?.storeCompliance?.dln || "",
      fssaiNo: storeDetails?.storeCompliance?.fssaiNo || "",
    },
    address: {
      premisesNo: storeDetails?.address?.premisesNo || "",
      floor: storeDetails?.address?.floor || "",
      premisesName: storeDetails?.address?.premisesName || "",
      landmark: storeDetails?.address?.landmark || "",
      areaRoad: storeDetails?.address?.areaRoad || "",
      city: storeDetails?.address?.city || "",
      pinCode: storeDetails?.address?.pinCode || "",
      state: storeDetails?.address?.state || "",
      phoneNumber: storeDetails?.address?.phoneNumber || "",
    },
    location: {
      googleMapUrl: storeDetails?.location?.googleMapUrl || "",
    },
    contact: {
      contactNo: storeDetails?.contact?.contactNo || "",
      email: storeDetails?.contact?.email || "",
      email2: storeDetails?.contact?.email2 || "",
    },
    billing: {
      applyAll: false,
      smallCartFee: {
        charges: storeDetails?.storeBillingDetail?.smallCartFee?.charges || 0,
        applyBelow:
          storeDetails?.storeBillingDetail?.smallCartFee?.applyBelow || 0,
        enable: storeDetails?.storeBillingDetail?.smallCartFee?.enable || false,
      },
      handlingFee: {
        charges: storeDetails?.storeBillingDetail?.handlingFee?.charges || 0,
        applyBelow:
          storeDetails?.storeBillingDetail?.handlingFee?.applyBelow || 0,
        enable: storeDetails?.storeBillingDetail?.handlingFee?.enable || false,
      },
      deliveryChargesSameCity: {
        charges:
          storeDetails?.storeBillingDetail?.deliveryChargesSameCity?.charges ||
          0,
        applyBelow:
          storeDetails?.storeBillingDetail?.deliveryChargesSameCity
            ?.applyBelow || 0,
        enable:
          storeDetails?.storeBillingDetail?.deliveryChargesSameCity?.enable ||
          false,
      },
      deliveryChargesSameState: {
        charges:
          storeDetails?.storeBillingDetail?.deliveryChargesSameState?.charges ||
          0,
        applyBelow:
          storeDetails?.storeBillingDetail?.deliveryChargesSameState
            ?.applyBelow || 0,
        enable:
          storeDetails?.storeBillingDetail?.deliveryChargesSameState?.enable ||
          false,
      },
      deliveryChargesOtherState: {
        charges:
          storeDetails?.storeBillingDetail?.deliveryChargesOtherState
            ?.charges || 0,
        applyBelow:
          storeDetails?.storeBillingDetail?.deliveryChargesOtherState
            ?.applyBelow || 0,
        enable:
          storeDetails?.storeBillingDetail?.deliveryChargesOtherState?.enable ||
          false,
      },
      noDiscount: {
        percentage:
          storeDetails?.storeBillingDetail?.noDiscount?.percentage || 0,
        applyBelow:
          storeDetails?.storeBillingDetail?.noDiscount?.applyBelow || 0,
        enable: storeDetails?.storeBillingDetail?.noDiscount?.enable || false,
      },
    },
  };

  useEffect(() => {
    dispatch(fetchStores());
  }, [dispatch]);

  const handleRemoveStore = async (values, resetForm) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete your account?"
    );

    if (confirmDelete) {
      const response = await dispatch(
        removeStore(values.storeDetails.SID)
      ).unwrap();
      if (response?.status === 200) {
        dispatch(setStoreById({}));
        setSelectedStore({});
        resetForm();
        toast.success("Successfully deleted store.");
      } else {
        toast.error(response?.message);
      }
    }
  };

  // const handleViewDashBoard = () => {
  //   const tabChangeEventStore = new Event("tabChange");
  //   window.dispatchEvent(tabChangeEventStore);
  //   dispatch(setChangeTab({ name: "Listing", index: 0 }));
  // };
  const handleCheckboxChange = async (e, fieldPath, setFieldValue, values) => {
    let toggledEnableValue;

    if (fieldPath === "applyAll") {
      setFieldValue("billing.applyAll", true);
    } else {
      const currentEnableValue = values?.billing?.[fieldPath]?.enable;

      toggledEnableValue = !currentEnableValue;
      setFieldValue("billing.applyAll");
      setFieldValue(`billing.${fieldPath}.enable`, toggledEnableValue);
    }

    const storeId = values?.storeDetails?.SID;

    try {
      let updatedData;
      if (fieldPath === "applyAll") {
        updatedData = {
          billing: {
            ...values.billing,
            applyAll: true,
          },
        };
      } else {
        updatedData = {
          billing: {
            ...values.billing,
            applyAll: false,
            [fieldPath]: {
              ...values?.billing[fieldPath],
              enable: toggledEnableValue,
            },
          },
        };
      }

      const response = await dispatch(
        modifyStore({ storeId, updatedData })
      ).unwrap();

      if (response.status === 200) {
        toast.success("Store updated successfully");
        setFieldValue("billing.applyAll", false);
        // setSelectedStore("");
        // formik.resetForm({
        //   values: initialValues,
        // });
      } else {
        // Handle failure if needed
      }
    } catch (error) {
      console.error("Complete Error Object:", error);

      if (error.response) {
        if (error) {
          console.error("API Error:", error.error);
        } else {
          console.error("Unexpected Error:", error);
          toast.error("An unexpected error occurred.");
        }
      }
    }
  };

  const handleEditToggle = () => {
    setShowEditIcon(true);
  };

  const handleEditIconClick = (fieldName) => {
    if (editingField !== fieldName) {
      setEditingField(fieldName);
      setTimeout(() => handleFocus(fieldName), 0);
    }
  };

  const handleBlur = (fieldName) => {
    if (editingField === fieldName) {
      setEditingField(null);
    }
  };
  const handleFocus = (fieldName) => {
    const inputField = document.getElementById(fieldName);

    if (inputField) {
      inputField.focus();
    }
  };

  let isDisabled = Object.keys(storeById)?.length ? true : false;
  return (
    <>
      <section className="tab-store">
        <Loader loading={loading} />
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={handleStoreSubmit}
          validationSchema={createStoreValidation}
          validateOnChange={true}
        >
          {({
            values,
            setValues,
            resetForm,
            handleChange,
            setFieldValue,
            touched,
            errors,
            setFieldTouched,
          }) => {
            return (
              <Form className="dashboard-container">
                {/* <form className="dashboard-container" onSubmit={formik.handleSubmit}> */}
                <div className="flex item-center justify">
                  <div className="w-full mb-3">
                    <SearchSelectStore
                      stores={stores}
                      setFieldValue={setFieldValue}
                      values={values}
                      selectedStore={selectedStore}
                      setSelectedStore={setSelectedStore}
                    />
                  </div>
                  {/* <div className="w-[180px]">
              <button
                type="button"
                onClick={handleViewDashBoard}
                className="w-full ml-2 bg-primary-600 text-white p-2 rounded h-full"
              >
                View Dashboard
              </button>
            </div> */}
                </div>
                {values?.storeDetails?.SID && (
                  <>
                    <div className="bg-gray-100 py-3 px-5 rounded-[12px] mb-4">
                      <PrimaryDetails
                        handleChange={handleChange}
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldTouched={setFieldTouched}
                        isEdit={showEditIcon}
                        handleEditIconClick={handleEditIconClick}
                        handleBlur={handleBlur}
                        editingField={editingField}
                        setShowEditIcon={setShowEditIcon}
                        isDisabled={isDisabled}
                      />
                      {/* <Compliances formik={formik} />
                      <Address formik={formik} />
                      <Location formik={formik} />
                      <Contact formik={formik} /> */}
                      <Compliances
                        handleChange={handleChange}
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldTouched={setFieldTouched}
                        isEdit={showEditIcon}
                        handleEditIconClick={handleEditIconClick}
                        handleBlur={handleBlur}
                        editingField={editingField}
                        setShowEditIcon={setShowEditIcon}
                        isDisabled={isDisabled}
                      />
                      <Address
                        handleChange={handleChange}
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldTouched={setFieldTouched}
                        isEdit={showEditIcon}
                        handleEditIconClick={handleEditIconClick}
                        handleBlur={handleBlur}
                        editingField={editingField}
                        setShowEditIcon={setShowEditIcon}
                        isDisabled={isDisabled}
                      />
                      <Location
                        handleChange={handleChange}
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldTouched={setFieldTouched}
                        isEdit={showEditIcon}
                        handleEditIconClick={handleEditIconClick}
                        handleBlur={handleBlur}
                        editingField={editingField}
                        setShowEditIcon={setShowEditIcon}
                        isDisabled={isDisabled}
                      />
                      <Contact
                        handleChange={handleChange}
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldTouched={setFieldTouched}
                        isEdit={showEditIcon}
                        handleEditIconClick={handleEditIconClick}
                        handleBlur={handleBlur}
                        editingField={editingField}
                        setShowEditIcon={setShowEditIcon}
                        isDisabled={isDisabled}
                      />
                      <div className="flex items-center gap-2 pt-2">
                        {!showEditIcon ? (
                          <Button
                            className="bg-primary-600 h-[36px] text-white py-2 px-5 min-w-[136px] text-[13px] leading-[20px] rounded-full flex items-center justify-center gap-2 shadow-none outline-none"
                            // onClick={handleUpdateDoctor}
                            onClick={handleEditToggle}
                            type="button"
                          >
                            <Edit />
                            Edit Details
                          </Button>
                        ) : (
                          <Button
                            className="border-primary-600 border text-secondry-600 h-[36px] py-2 px-5 min-w-[136px] text-[13px] leading-[20px] rounded-full flex items-center justify-center gap-2 shadow-none outline-none"
                            onClick={() => {
                              resetForm();
                              setShowEditIcon(false);
                            }}
                            type="button"
                          >
                            {/* <Edit fill="#7C6060" /> */}
                            Cancel
                          </Button>
                        )}
                        {/* <Button
                          className="border-primary-600 border text-secondry-600 h-[36px] py-2 px-5 min-w-[136px] text-[13px] leading-[20px] rounded-full flex items-center justify-center gap-2 shadow-none outline-none"
                          onClick={() => handleStoreSubmit(values, resetForm)}
                          type="button"
                        >
                          <Edit fill="#7C6060" />
                          Edit Details
                        </Button> */}
                        {!showEditIcon ? (
                          <Button
                            type="button"
                            className="border-primary-600 border text-secondry-600 h-[36px] py-2 px-5 min-w-[136px] text-[13px] leading-[20px] rounded-full flex items-center justify-center gap-2 shadow-none outline-none"
                            onClick={() => handleRemoveStore(values, resetForm)}
                          >
                            {/* <img alt="warning" src="/icons/warning.svg" /> */}
                            <Warning />
                            Delete Account
                          </Button>
                        ) : (
                          <Button
                            className="border-primary-600 border text-secondry-600 h-[36px] py-2 px-5 min-w-[136px] text-[13px] leading-[20px] rounded-full flex items-center justify-center gap-2 shadow-none outline-none"
                            // onClick={() => handleStoreSubmit(values, resetForm)}
                            type="submit"
                          >
                            <Edit fill="#7C6060" />
                            Save Details
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="pt-3">
                      <BillingSettings
                        handleChange={handleChange}
                        values={values}
                        setFieldValue={setFieldValue}
                        handleCheckboxChange={handleCheckboxChange}
                        storeById={storeById}
                        initialValues={initialValues}
                      />
                    </div>
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </section>
    </>
  );
};

export default Store;
